import { Button as ChakraButton, type ButtonProps as ChakraButtonProps } from '@chakra-ui/react'
import { ReactElement, forwardRef } from 'react'
import { Divider, Flex, Menu } from '.'

export interface Props extends Omit<ChakraButtonProps, 'variant' | 'justify'> {
  /** default center */
  justify?: 'center' | 'space-between'
  leftIcon?: ReactElement
  palette?: 'primary' | 'secondary' | 'danger'
  rightIcon?: ReactElement
  /** default solid */
  variant?: 'solid' | 'outline' | 'ghost' | 'link'
  /** Add split button with more options */
  moreOptions?: ButtonMoreOption[]
}

export interface ButtonMoreOption {
  disabled?: boolean
  label: string
  onClick: () => void
}

export default forwardRef<HTMLButtonElement, Props>(
  (
    {
      bg,
      background,
      backgroundColor,
      children,
      color,
      moreOptions,
      justify = 'center',
      leftIcon,
      palette,
      rightIcon,
      size = 'md',
      variant = 'solid',
      ...rest
    },
    ref
  ) => {
    const baseBorderColor = { base: 'rgba(23, 23, 23, 0.2)', _dark: 'rgba(255, 255, 255, 0.2)' }
    const textColor =
      color ??
      (variant === 'solid'
        ? palette
          ? 'white'
          : undefined
        : palette
          ? `${palette}.500`
          : undefined)
    const sizeStyles =
      typeof size === 'string' && size in buttonSizeStyles
        ? buttonSizeStyles[size as ButtonSizeKey]
        : undefined

    return (
      <ChakraButton
        ref={ref}
        bg={
          (bg || background || backgroundColor) ??
          (variant === 'solid'
            ? palette
              ? `${palette}.500`
              : { base: 'white', _dark: 'rgba(255, 255, 255, 0.14)' }
            : variant === 'outline' && !palette
              ? { base: 'white', _dark: 'rgba(255, 255, 255, 0.05)' }
              : 'transparent')
        }
        color={textColor}
        borderColor={
          variant === 'outline' ? (palette ? `${palette}.500` : baseBorderColor) : undefined
        }
        borderRadius="md"
        borderWidth={variant === 'outline' ? `1px` : undefined}
        boxShadow={
          variant === 'solid' || variant === 'outline'
            ? '0px 1px 4px 0px rgba(0, 0, 0, 0.06)'
            : undefined
        }
        fontWeight="medium"
        _hover={{
          background:
            variant === 'solid'
              ? palette
                ? `${palette}.700`
                : { base: '#FFFFFF88', _dark: 'rgba(255, 255, 255, 0.24)' }
              : variant === 'link'
                ? undefined
                : palette
                  ? palette === 'primary'
                    ? 'rgba(60, 75, 211, 0.03)'
                    : `${palette}.200`
                  : baseBorderColor,
          textDecoration: variant === 'link' ? 'underline' : undefined,
        }}
        size={size}
        pr={moreOptions?.length ? 0 : undefined}
        {...sizeStyles}
        {...rest}
      >
        {leftIcon}
        {children && (
          <Flex overflow="hidden" fontWeight="semibold" flex={justify === 'center' ? undefined : 1}>
            {children}
          </Flex>
        )}
        {rightIcon}
        {!!moreOptions?.length && (
          <Flex>
            <Divider my={1} orientation="vertical" borderColor={textColor} alignSelf="stretch" />
            <Flex onClick={e => e.stopPropagation()}>
              <Menu
                as="span"
                disabled={moreOptions.every(o => o.disabled)}
                background="transparent"
                border="none"
                buttonLabel={false}
                color={textColor}
                hideCheckbox
                onChange={v => moreOptions.find(o => o.label === v)?.onClick()}
                options={moreOptions.map(o => ({ type: 'item', label: o.label, value: o.label }))}
                px={2}
                size="sm"
              />
            </Flex>
          </Flex>
        )}
      </ChakraButton>
    )
  }
)

const buttonSizeStyles = {
  lg: { fontSize: 'md', h: 10, minW: 12, px: 5 },
  md: { fontSize: 'sm', h: 9, minW: 10, px: 2 },
  sm: { fontSize: 'xs', h: 7, minW: 8, px: 3 },
  xl: { fontSize: 'lg', h: 12, minW: 14, px: 6 },
  xs: { fontSize: 'xs', h: 6, minW: 6, px: 2.5 },
} as const

type ButtonSizeKey = keyof typeof buttonSizeStyles
